import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import {
  ChevronLeft,
  Clock,
  Eye,
  Heart,
  MessageCircle,
  Tag,
  Search,
  Loader2,
  BookOpen,
} from 'lucide-react';
import { useBlogPosts, useFeaturedBlogPosts, useBlogCategories, useBlogPost } from '@/hooks/useBlog';
import { BlogCardSkeleton } from '@/components/Skeleton';
import { timeAgo, formatCount } from '@/lib/formatters';
import { processAvatarUrl } from '@/lib/mediaUtils';
import type { BlogPostListItem, BlogCategory } from '@cc-consulting-nv/ccsdk';

/* ─── Blog list card ─── */

function BlogCard({ post, onClick }: { post: BlogPostListItem; onClick: () => void }) {
  const coverImg = post.ogImage || (post as Record<string, unknown>).featuredImage as string | undefined;
  const categoryName = post.category?.name;
  const authorName = (post.author as Record<string, unknown> | null)?.displayName as string
    || (post.author as Record<string, unknown> | null)?.name as string
    || (post.author as Record<string, unknown> | null)?.username as string
    || '';
  const published = post.publishedAt ? timeAgo(post.publishedAt) : '';

  return (
    <button
      onClick={onClick}
      className="w-full bg-[#141821] rounded-2xl overflow-hidden text-left active:scale-[0.99] transition-transform"
    >
      {coverImg ? (
        <img src={coverImg} alt={post.title} className="w-full h-44 object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-44 bg-gradient-to-br from-[#A38BFF]/20 to-[#141821] flex items-center justify-center">
          <BookOpen size={40} className="text-[#A38BFF]/40" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {categoryName && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[#A38BFF] bg-[#A38BFF]/10 px-2 py-0.5 rounded-full">
              {categoryName}
            </span>
          )}
          {post.readingTimeMinutes && (
            <span className="text-xs text-[#A7B0C8] flex items-center gap-1">
              <Clock size={11} />
              {post.readingTimeMinutes}m read
            </span>
          )}
        </div>
        <h3 className="text-sm font-bold text-[#F5F7FF] leading-snug mb-1 line-clamp-2">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-xs text-[#A7B0C8] line-clamp-2 mb-2">{post.excerpt}</p>
        )}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1.5">
            {authorName && <span className="text-xs text-[#A7B0C8]">{authorName}</span>}
            {published && <span className="text-xs text-[#A7B0C8]">· {published}</span>}
          </div>
          <div className="flex items-center gap-3 text-[#A7B0C8]">
            <span className="flex items-center gap-1 text-xs">
              <Eye size={12} />
              {formatCount(post.engagement?.viewCount ?? 0)}
            </span>
            <span className="flex items-center gap-1 text-xs">
              <Heart size={12} />
              {formatCount(post.engagement?.reactionCount ?? 0)}
            </span>
            <span className="flex items-center gap-1 text-xs">
              <MessageCircle size={12} />
              {formatCount(post.engagement?.commentCount ?? 0)}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ─── Blog List page ─── */

interface BlogListProps {
  onBack?: () => void;
}

export function BlogList({ onBack }: BlogListProps) {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const headerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data: categories } = useBlogCategories();
  const { data: featured } = useFeaturedBlogPosts();

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBlogPosts(activeCategory ? { category: activeCategory } : undefined);

  const posts = data?.pages.flatMap((p) => p.data) ?? [];
  const featuredPost = featured?.[0];

  useEffect(() => {
    gsap.fromTo(headerRef.current, { y: -20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, ease: 'power2.out' });
    gsap.fromTo(listRef.current, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.4, delay: 0.1, ease: 'power2.out' });
  }, []);

  // Stagger cards when list changes
  useEffect(() => {
    if (listRef.current && listRef.current.children.length > 0) {
      gsap.fromTo(
        listRef.current.children,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.3, stagger: 0.05, ease: 'power2.out' }
      );
    }
  }, [activeCategory, data]);

  // Infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const filteredPosts = searchQuery
    ? posts.filter((p) => p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()))
    : posts;

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-24">
      {/* Header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 pt-safe"
      >
        <div className="flex items-center gap-3 mb-3">
          {onBack && (
            <button aria-label="Back" onClick={onBack} className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8] flex-shrink-0">
              <ChevronLeft size={22} />
            </button>
          )}
          <h1 className="text-xl font-bold text-[#F5F7FF] flex-1" style={{ fontFamily: 'Space Grotesk' }}>Articles</h1>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8] pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles..."
            className="w-full h-9 bg-[#141821] rounded-xl pl-9 pr-4 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/50 outline-none focus:ring-1 focus:ring-[#A38BFF]/50"
          />
        </div>

        {/* Category pills */}
        {categories && categories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-0.5">
            <button
              onClick={() => setActiveCategory(null)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!activeCategory ? 'bg-[#A38BFF] text-[#0B0D10]' : 'bg-[#141821] text-[#A7B0C8]'}`}
            >
              All
            </button>
            {(categories as BlogCategory[]).map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(activeCategory === cat.slug ? null : cat.slug)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeCategory === cat.slug ? 'bg-[#A38BFF] text-[#0B0D10]' : 'bg-[#141821] text-[#A7B0C8]'}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Featured post */}
        {featuredPost && !activeCategory && !searchQuery && (
          <div className="mb-2">
            <p className="text-xs font-semibold text-[#A38BFF] uppercase tracking-wider mb-2">Featured</p>
            <BlogCard post={featuredPost} onClick={() => navigate(`/blog/${featuredPost.slug}`)} />
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <BlogCardSkeleton key={i} />)}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen size={48} className="text-[#2A2F3A] mx-auto mb-4" />
            <p className="text-[#A7B0C8]">{searchQuery ? 'No articles match your search' : 'No articles yet'}</p>
          </div>
        ) : (
          <div ref={listRef} className="space-y-4">
            {filteredPosts
              .filter((p) => !featuredPost || p.slug !== featuredPost.slug || activeCategory || searchQuery)
              .map((post) => (
                <BlogCard key={post.slug} post={post} onClick={() => navigate(`/blog/${post.slug}`)} />
              ))}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <Loader2 size={20} className="animate-spin text-[#A38BFF]" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Blog Detail page ─── */

interface BlogDetailProps {
  onBack?: () => void;
}

export function BlogDetail({ onBack }: BlogDetailProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: post, isLoading } = useBlogPost(slug);

  useEffect(() => {
    gsap.fromTo(headerRef.current, { y: -20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, ease: 'power2.out' });
    if (contentRef.current) {
      gsap.fromTo(contentRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, delay: 0.1, ease: 'power2.out' });
    }
  }, [post]);

  const handleBack = () => { if (onBack) onBack(); else navigate(-1); };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0B0D10]">
        <div className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 pt-safe flex items-center gap-3">
          <button aria-label="Back" onClick={handleBack} className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <ChevronLeft size={22} />
          </button>
        </div>
        <div className="flex justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[#A38BFF]" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex flex-col items-center justify-center gap-4">
        <p className="text-[#A7B0C8]">Article not found.</p>
        <button onClick={handleBack} className="text-[#A38BFF] text-sm">Go back</button>
      </div>
    );
  }

  const coverImg = post.ogImage || post.images?.[0]?.url;
  const authorName = post.authors?.[0]
    ? (post.authors[0] as Record<string, unknown>).displayName as string
      || (post.authors[0] as Record<string, unknown>).name as string
      || (post.authors[0] as Record<string, unknown>).username as string
      || ''
    : '';
  const rawAuthorAvatar = (post.authors?.[0] as unknown as Record<string, unknown> | undefined)?.avatarUrl as string
    || (post.authors?.[0] as unknown as Record<string, unknown> | undefined)?.avatar as string
    || '';
  const authorAvatar = processAvatarUrl(rawAuthorAvatar) || '';
  const published = post.publishedAt ? timeAgo(post.publishedAt) : '';

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-20">
      {/* Header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 pt-safe flex items-center gap-3"
      >
        <button aria-label="Back" onClick={handleBack} className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8] flex-shrink-0">
          <ChevronLeft size={22} />
        </button>
        <h1 className="text-sm font-semibold text-[#F5F7FF] line-clamp-1 flex-1" style={{ fontFamily: 'Space Grotesk' }}>
          {post.category?.name || 'Article'}
        </h1>
        <div className="flex items-center gap-3 text-xs text-[#A7B0C8]">
          <span className="flex items-center gap-1"><Eye size={13} />{formatCount(post.engagement?.viewCount ?? 0)}</span>
        </div>
      </div>

      <div ref={contentRef} className="px-4 pt-6 pb-12">
        {/* Cover image */}
        {coverImg && (
          <img src={coverImg} alt={post.title} className="w-full max-h-64 object-cover rounded-2xl mb-6" loading="lazy" />
        )}

        {/* Category + read time */}
        <div className="flex items-center gap-2 mb-3">
          {post.category && (
            <span className="text-xs font-semibold uppercase tracking-wider text-[#A38BFF] bg-[#A38BFF]/10 px-2 py-0.5 rounded-full">
              {post.category.name}
            </span>
          )}
          {post.readingTimeMinutes && (
            <span className="text-xs text-[#A7B0C8] flex items-center gap-1">
              <Clock size={11} /> {post.readingTimeMinutes}m read
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-[#F5F7FF] leading-tight mb-4" style={{ fontFamily: 'Space Grotesk' }}>
          {post.title}
        </h1>

        {/* Author + date */}
        {(authorName || published) && (
          <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/[0.06]">
            {authorAvatar ? (
              <img src={authorAvatar} alt={authorName} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : authorName ? (
              <div className="w-9 h-9 rounded-full bg-[#A38BFF]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-[#A38BFF]">{authorName[0]?.toUpperCase()}</span>
              </div>
            ) : null}
            <div>
              {authorName && <p className="text-sm font-medium text-[#F5F7FF]">{authorName}</p>}
              {published && <p className="text-xs text-[#A7B0C8]">{published}</p>}
            </div>
          </div>
        )}

        {/* HTML content */}
        {post.contentHtml ? (
          <div
            className="blog-content text-[#E0E4F0] leading-relaxed text-sm"
            dangerouslySetInnerHTML={{ __html: post.contentHtml }}
          />
        ) : post.excerpt ? (
          <p className="text-[#E0E4F0] leading-relaxed">{post.excerpt}</p>
        ) : null}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-5 border-t border-white/[0.06]">
            {post.tags.map((tag) => (
              <span key={tag} className="flex items-center gap-1 text-xs text-[#A7B0C8] bg-[#141821] px-3 py-1.5 rounded-full">
                <Tag size={11} /> {tag}
              </span>
            ))}
          </div>
        )}

        {/* Engagement */}
        <div className="flex items-center gap-5 mt-6 pt-5 border-t border-white/[0.06] text-[#A7B0C8]">
          <span className="flex items-center gap-1.5 text-sm"><Heart size={18} />{formatCount(post.engagement?.reactionCount ?? 0)}</span>
          <span className="flex items-center gap-1.5 text-sm"><MessageCircle size={18} />{formatCount(post.engagement?.commentCount ?? 0)}</span>
          <span className="flex items-center gap-1.5 text-sm"><Eye size={18} />{formatCount(post.engagement?.viewCount ?? 0)}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Default export for BottomNav routing ─── */
export default BlogList;
